import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth0Data } from '../../context/Auth0DataContext';
import TeamSwitcher from '../TeamSwitcher/TeamSwitcher';

const { FiMenu, FiX, FiHome, FiDatabase, FiTrello, FiTarget, FiUser, FiLogOut, FiZap } = FiIcons;

const Layout = ({ children }) => {
  const { user, signOut } = useAuth0Data();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Dashboard', href: '/', icon: FiHome },
    { name: 'Data Management', href: '/data-management', icon: FiDatabase },
    { name: 'Project Management', href: '/project-management', icon: FiTrello },
    { name: 'CRM Pipeline', href: '/crm', icon: FiTarget },
  ];

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const handleSignOut = async () => {
    await signOut();
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Desktop Header Navigation - Fixed but not frozen */}
      <header className="hidden lg:block sticky top-0 z-40 glass border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <motion.div 
              className="flex items-center" 
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }}
            >
              <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg">
                <SafeIcon icon={FiZap} className="w-5 h-5 text-white" />
              </div>
              <span className="ml-3 text-lg font-bold gradient-text">FuelDash</span>
            </motion.div>

            {/* Desktop Navigation */}
            <nav className="flex items-center space-x-1">
              {navigation.map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={item.href}
                    className={`group flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      isActive(item.href)
                        ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/25'
                        : 'text-gray-700 hover:bg-white/60 hover:text-gray-900'
                    }`}
                  >
                    <SafeIcon 
                      icon={item.icon} 
                      className={`w-4 h-4 mr-2 transition-transform group-hover:scale-110 ${
                        isActive(item.href) ? 'text-white' : 'text-gray-500'
                      }`} 
                    />
                    {item.name}
                    {isActive(item.href) && (
                      <motion.div
                        layoutId="activeDesktopTab"
                        className="ml-2 w-1.5 h-1.5 bg-white rounded-full"
                      />
                    )}
                  </Link>
                </motion.div>
              ))}
            </nav>

            {/* Team Switcher and User Menu */}
            <motion.div
              className="flex items-center gap-4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              {/* Team Switcher */}
              <TeamSwitcher />

              {/* User Menu */}
              <div className="flex items-center p-2 rounded-xl hover:bg-white/60 transition-colors cursor-pointer group">
                <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiUser} className="w-4 h-4 text-white" />
                </div>
                <div className="ml-2 hidden sm:block">
                  <p className="text-sm font-medium text-gray-800">
                    {user?.name || user?.email?.split('@')[0] || 'User'}
                  </p>
                </div>
                <button
                  onClick={handleSignOut}
                  className="ml-2 p-1.5 text-gray-400 group-hover:text-red-600 transition-colors rounded-lg hover:bg-red-50"
                  title="Sign Out"
                >
                  <SafeIcon icon={FiLogOut} className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </header>

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Mobile Sidebar */}
      <motion.div
        initial={false}
        animate={{ x: sidebarOpen ? 0 : -280 }}
        className="fixed inset-y-0 left-0 z-50 w-72 glass lg:hidden border-r border-white/20 flex flex-col"
      >
        <div className="flex h-full flex-col">
          {/* Mobile Logo */}
          <div className="flex h-16 items-center justify-between px-6 border-b border-white/10">
            <motion.div 
              className="flex items-center" 
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }}
            >
              <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg">
                <SafeIcon icon={FiZap} className="w-5 h-5 text-white" />
              </div>
              <span className="ml-3 text-lg font-bold gradient-text">FuelDash</span>
            </motion.div>
            <button
              onClick={closeSidebar}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <SafeIcon icon={FiX} className="w-4 h-4 text-gray-600" />
            </button>
          </div>

          {/* Mobile Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2">
            {navigation.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={item.href}
                  className={`group flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
                    isActive(item.href)
                      ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/25'
                      : 'text-gray-700 hover:bg-white/60 hover:text-gray-900'
                  }`}
                  onClick={closeSidebar}
                >
                  <SafeIcon 
                    icon={item.icon} 
                    className={`w-5 h-5 mr-3 transition-transform group-hover:scale-110 ${
                      isActive(item.href) ? 'text-white' : 'text-gray-500'
                    }`} 
                  />
                  {item.name}
                  {isActive(item.href) && (
                    <motion.div
                      layoutId="activeMobileTab"
                      className="ml-auto w-2 h-2 bg-white rounded-full"
                    />
                  )}
                </Link>
              </motion.div>
            ))}
          </nav>

          {/* Mobile User section */}
          <motion.div 
            className="border-t border-white/10 p-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="flex items-center p-3 rounded-xl hover:bg-white/60 transition-colors cursor-pointer group">
              <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiUser} className="w-5 h-5 text-white" />
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-gray-800">
                  {user?.name || user?.email?.split('@')[0] || 'User'}
                </p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
              <button
                onClick={handleSignOut}
                className="p-2 text-gray-400 group-hover:text-red-600 transition-colors rounded-lg hover:bg-red-50"
                title="Sign Out"
              >
                <SafeIcon icon={FiLogOut} className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Mobile header (hamburger menu) */}
      <header className="lg:hidden sticky top-0 z-30 glass border-b border-white/10">
        <div className="flex h-14 items-center justify-between px-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-white/60 transition-colors"
          >
            <SafeIcon icon={FiMenu} className="w-5 h-5 text-gray-700" />
          </motion.button>
          
          <div className="flex items-center">
            <div className="w-6 h-6 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiZap} className="w-4 h-4 text-white" />
            </div>
            <span className="ml-2 text-sm font-bold gradient-text">FuelDash</span>
          </div>
          
          <div className="w-10"></div> {/* Spacer for centering */}
        </div>
      </header>

      {/* Main content area */}
      <main className="w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;